import { Plus, X } from 'lucide-react';
import {
  ATTACHMENT_ACCEPT,
  attachedFilesIn,
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RichTextEditor,
  screenAttachments,
  SearchableSelect,
  searchableSource,
  toast,
  type RichTextAttachments,
} from '@eo/shared';

import type { QueueAssignee } from './api/types';
import { AssigneeChip } from './AssigneeChip';
import {
  NO_PARENT_VALUE,
  NO_PRIORITY_VALUE,
  NO_TASK_TYPE_VALUE,
  PRIORITY_OPTIONS,
} from './constants';
import { assigneeChips } from './helpers';
import type { TaskEditorDialogProps } from './types';

// Everything a CFO can change about a proposed task, in one place. The card
// stays a thing you read and decide on; correcting it is a deliberate step.
export function TaskEditorDialog({
  draft,
  options,
  members,
  onChange,
  onClose,
  onOpenMembers,
  onQueryOptions,
}: TaskEditorDialogProps) {
  if (!draft) return null;

  const clientSearch = searchableSource(options.clients, (q) =>
    onQueryOptions({ source: 'clients', ...q }),
  );
  const taskTypeSearch = searchableSource(options.taskTypes, (q) =>
    onQueryOptions({ source: 'taskTypes', ...q }),
  );
  const parentSearch = searchableSource(options.tasks, (q) =>
    onQueryOptions({ source: 'tasks', ...q, filters: { clientId: draft.clientId ?? '' } }),
  );

  const canPickFromList = Boolean(draft.clientId);
  // A comment can be addressed to one person; with several assignees there is
  // no one to hand it to, and ClickUp notifies them all regardless.
  const soleAssignee = draft.assignees.length === 1 ? draft.assignees[0] : null;

  // Both fields send their files with the one approval, so the size limit is
  // counted across them.
  const attachments: RichTextAttachments = {
    accept: ATTACHMENT_ACCEPT,
    onAttach: (files) => {
      const attached = attachedFilesIn(`${draft.context}\n${draft.comment ?? ''}`);
      const { accepted, refused } = screenAttachments(
        files,
        attached.map((a) => a.file),
      );
      for (const reason of refused) toast.error(reason);
      return accepted;
    },
  };

  const toggleAssignee = (member: QueueAssignee) => {
    const present = draft.assignees.some((a) => a.id === member.id);
    onChange({
      assignees: present
        ? draft.assignees.filter((a) => a.id !== member.id)
        : [...draft.assignees, member],
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit proposed task</DialogTitle>
          <DialogDescription>
            {draft.meetingTitle ? `From ${draft.meetingTitle}.` : 'No meeting recorded.'} Changes
            apply when you approve it.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div>
            <Label className="text-xs" htmlFor={`name-${draft.taskId}`}>
              Task name
            </Label>
            <Input
              id={`name-${draft.taskId}`}
              value={draft.taskName}
              aria-label="Task name"
              onChange={(e) => onChange({ taskName: e.target.value })}
              className="h-9 text-sm"
            />
          </div>

          <div>
            <Label className="text-xs" htmlFor={`context-${draft.taskId}`}>
              Context
            </Label>
            <RichTextEditor
              id={`context-${draft.taskId}`}
              value={draft.context}
              ariaLabel="Context"
              attachments={attachments}
              onChange={(context) => onChange({ context })}
            />
          </div>

          <div>
            <Label className="text-xs">Client</Label>
            <SearchableSelect
              value={draft.clientId ?? ''}
              selectedLabel={draft.client}
              ariaLabel="Client"
              placeholder="Pick a client"
              options={options.clients.items.map((c) => ({ value: c.id, label: c.name }))}
              {...clientSearch}
              onChange={(id) => {
                const client = options.clients.items.find((c) => c.id === id);
                if (client) onChange({ client: client.name, clientId: client.id });
              }}
            />
          </div>

          <div>
            <Label className="text-xs">Task type</Label>
            <SearchableSelect
              value={draft.taskTypeId === null ? NO_TASK_TYPE_VALUE : draft.taskTypeId}
              selectedLabel={draft.taskTypeName ?? undefined}
              ariaLabel="Task type"
              placeholder="No task type"
              options={[
                { value: NO_TASK_TYPE_VALUE, label: 'No task type', pinned: true },
                ...options.taskTypes.items.map((t) => ({ value: t.id, label: t.name })),
              ]}
              {...taskTypeSearch}
              onChange={(value) =>
                onChange({
                  taskTypeId: value === NO_TASK_TYPE_VALUE ? null : value,
                  taskTypeName: options.taskTypes.items.find((t) => t.id === value)?.name ?? null,
                })
              }
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Start date</Label>
              <DatePicker
                value={draft.startDate}
                onChange={(value) => onChange({ startDate: value })}
                className="text-sm"
                ariaLabel="Start date"
              />
            </div>
            <div>
              <Label className="text-xs">Working date</Label>
              <DatePicker
                value={draft.workingDate}
                onChange={(value) => onChange({ workingDate: value })}
                className="text-sm"
                ariaLabel="Working date"
              />
            </div>
            <div>
              <Label className="text-xs">Due date</Label>
              <DatePicker
                value={draft.dueDate}
                onChange={(value) => onChange({ dueDate: value })}
                className="text-sm"
                ariaLabel="Due date"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Priority</Label>
              <SearchableSelect
                value={draft.priority === null ? NO_PRIORITY_VALUE : String(draft.priority)}
                ariaLabel="Priority"
                placeholder="No priority"
                options={[
                  { value: NO_PRIORITY_VALUE, label: 'No priority', pinned: true },
                  ...PRIORITY_OPTIONS.map((p) => ({ value: String(p.value), label: p.label })),
                ]}
                onChange={(value) =>
                  onChange({ priority: value === NO_PRIORITY_VALUE ? null : Number(value) })
                }
              />
            </div>
            <div>
              <Label className="text-xs" htmlFor={`estimate-${draft.taskId}`}>
                Estimate (min)
              </Label>
              <Input
                id={`estimate-${draft.taskId}`}
                type="number"
                min={0}
                value={draft.timeEstimateMinutes ?? ''}
                onChange={(e) =>
                  onChange({
                    timeEstimateMinutes: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                className="h-8 text-sm"
              />
            </div>
          </div>

          {canPickFromList && (
            <div>
              <Label className="text-xs">Add as subtask of (optional)</Label>
              <SearchableSelect
                value={draft.parentTaskId ?? NO_PARENT_VALUE}
                selectedLabel={draft.parentTaskName ?? undefined}
                ariaLabel="Add as subtask of"
                placeholder="Create as new task"
                emptyLabel="No tasks match"
                options={[
                  { value: NO_PARENT_VALUE, label: 'Create as new task', pinned: true },
                  ...options.tasks.items.map((t) => ({ value: t.id, label: t.name })),
                ]}
                {...parentSearch}
                onChange={(value) =>
                  onChange({
                    parentTaskId: value === NO_PARENT_VALUE ? null : value,
                    parentTaskName: options.tasks.items.find((t) => t.id === value)?.name ?? null,
                  })
                }
              />
            </div>
          )}

          <div>
            <Label className="text-xs">Assignees</Label>
            <div className="flex flex-wrap items-center gap-1">
              {assigneeChips(draft.assignees).map(({ id, name, color }) => (
                <AssigneeChip
                  key={id}
                  name={name}
                  color={color}
                  onRemove={() =>
                    onChange({ assignees: draft.assignees.filter((a) => a.id !== id) })
                  }
                />
              ))}
              {canPickFromList && (
                <Popover
                  onOpenChange={(open) => {
                    if (open) onOpenMembers();
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 gap-1 px-2 text-xs"
                      aria-label="Add assignee"
                    >
                      <Plus className="size-3" /> Add
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-1" align="start">
                    {members === undefined ? (
                      <p className="px-2 py-1.5 text-xs text-text-tertiary">Loading members…</p>
                    ) : members.length === 0 ? (
                      <p className="px-2 py-1.5 text-xs text-text-tertiary">No members found.</p>
                    ) : (
                      members.map((m) => {
                        const selected = draft.assignees.some((a) => a.id === m.id);
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => toggleAssignee(m)}
                            className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-chip-hover"
                          >
                            {m.username}
                            {selected && <X className="size-3 text-text-tertiary" />}
                          </button>
                        );
                      })
                    )}
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs" htmlFor={`comment-${draft.taskId}`}>
              ClickUp task comment
            </Label>
            <RichTextEditor
              id={`comment-${draft.taskId}`}
              value={draft.comment}
              ariaLabel="ClickUp task comment"
              placeholder="What the assignee needs to know before starting."
              attachments={attachments}
              onChange={(comment) => onChange({ comment })}
            />
            <p className="mt-1 text-xs leading-4 text-text-tertiary">
              {soleAssignee
                ? `Goes to the task’s comments in ClickUp when you approve, addressed to ${soleAssignee.username}.`
                : 'Goes to the task’s comments in ClickUp when you approve.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
